import React, { useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { getQueryParams } from '../common/util'

import { getConfig, getEndpoint, getExternalSocket } from './util'
import { determineUILocale } from '../common/labels'
import { OperatorsStateManager } from '../common/operators'
import { ConversationsStateManager } from '../common/conversations'

import ChatScreen from './ChatScreen'
import SplashScreen from './SplashScreen'
import MainMenu from './MainMenu'
import AppWrapper from './AppWrapper'
import AppState from './state'

// Check if we're on a mobile device
const isMobile = () => {
  return document.body.classList.contains('mobile')
}

// Animation variants for page transitions
const getPageVariants = isGoingHome => {
  const mobile = isMobile()

  if (mobile && !isGoingHome) {
    // Mobile: slide from right when navigating away from home
    return {
      initial: { x: '100%', opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: '100%', opacity: 0 },
    }
  }

  if (mobile && isGoingHome) {
    // Mobile: fade out when going back to home
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    }
  }

  // Desktop: simple fade
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  }
}

const App = props => {
  const [bot, setBot] = useState(null)
  const [locale, setLocale] = useState(null)
  const [online, setOnline] = useState(true)
  const [operators, setOperators] = useState([])
  const [conversations, setConversations] = useState([])
  const [, forceUpdate] = useState()

  const location = useLocation()
  const navigate = useNavigate()

  const operatorsManagerRef = useRef(null)
  const managerRef = useRef(null)

  const handleOnlineStatus = e => {
    setOnline(e.type === 'online')
  }

  const trackPageview = location => {
    if (window.ga && bot && bot.pwa && bot.pwa.ua_tracking_id) {
      ga('set', 'page', location.pathname + location.search)
      ga('send', 'pageview')
    }
  }

  const initialize = botData => {
    const newLocale = determineUILocale(null, botData)
    setBot(botData)
    setLocale(newLocale)

    const socket = getExternalSocket()
    const component = {
      state: { operators, conversations },
      setState: update => {
        if (update.operators !== undefined) setOperators(update.operators)
        if (update.conversations !== undefined) setConversations(update.conversations)
      },
    }

    operatorsManagerRef.current = new OperatorsStateManager(component, socket, botData, getConfig())
    managerRef.current = new ConversationsStateManager(component, socket, botData, {})

    AppState.setBot(botData)
    AppState.checkEmbedded()

    let add = document.location.search
    const params = getQueryParams()
    const { pathname } = document.location
    if (pathname && !pathname.match(/\/splash/) && !params.p) {
      add += (add ? '&' : '?') + 'p=' + encodeURIComponent(document.location.pathname)
    }

    if (AppState.hasSplash() && !AppState.splashDone()) {
      navigate('/splash' + add, { replace: true })
      return
    }
    if (AppState.hasIntro() && !AppState.introDone()) {
      navigate('/intro' + add, { replace: true })
      return
    }

    if (params.p) {
      navigate(params.p, { replace: true })
      return
    }

    // fix for Cordova not showing the chat on initial load
    setTimeout(() => forceUpdate({}), 0)
  }

  // Initialize on mount
  useEffect(() => {
    try {
      initialize(JSON.parse(getConfig().bot))
    } catch (e) {
      fetch(getEndpoint())
        .then(r => r.json())
        .then(botData => initialize(botData))
    }

    window.addEventListener('online', handleOnlineStatus)
    window.addEventListener('offline', handleOnlineStatus)

    return () => {
      window.removeEventListener('online', handleOnlineStatus)
      window.removeEventListener('offline', handleOnlineStatus)
      if (operatorsManagerRef.current) {
        operatorsManagerRef.current.componentWillUnmount()
      }
      if (managerRef.current) {
        managerRef.current.componentWillUnmount()
      }
    }
  }, [])

  // Track pageview changes
  useEffect(() => {
    trackPageview(location)
  }, [location, bot])

  const getScreenComponent = button => {
    return (
      <ChatScreen
        bot={bot}
        locale={locale}
        operators={operators}
        conversationManager={managerRef.current}
        button={button}
        {...props.chatScreenProps}
        online={online}
      />
    )
  }

  const renderMain = () => {
    if (AppState.hasStartScreen()) {
      return (
        <MainMenu
          bot={bot}
          locale={locale}
          operators={operators}
          conversations={conversations}
          conversationManager={managerRef.current}
        />
      )
    }

    const { pwa } = bot
    return getScreenComponent({ ...pwa, type: 'conversation', title: bot.title })
  }

  if (!bot) {
    return null
  }

  const { pwa } = bot
  const buttons = pwa.start_screen?.home_buttons || []

  const isGoingHome = location.pathname === '/'
  const pageVariants = getPageVariants(isGoingHome)

  const mobile = isMobile()

  return (
    <AppWrapper bot={bot} locale={locale}>
      <AnimatePresence mode={mobile ? 'wait' : undefined}>
        <motion.div
          key={location.pathname}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: isGoingHome ? 0 : 1,
          }}
        >
          <Routes location={location}>
            <Route path="/splash" element={<SplashScreen splash={pwa.splash_screen} bot={bot} locale={locale} />} />
            <Route path="/intro" element={getScreenComponent(pwa.intro_screen)} />
            {buttons.map((button, idx) => (
              <Route key={idx} path={'/b/' + idx} element={getScreenComponent(button)} />
            ))}
            <Route
              path="/g/:g"
              element={
                <GRoute
                  bot={bot}
                  locale={locale}
                  operators={operators}
                  manager={managerRef.current}
                  online={online}
                  chatScreenProps={props.chatScreenProps}
                />
              }
            />
            <Route path="/embed" element={renderMain()} />
            <Route path="/" element={renderMain()} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </AppWrapper>
  )
}

// Helper component for the /g/:g route to access params
const GRoute = ({ bot, locale, operators, manager, online, chatScreenProps }) => {
  const { g } = useParams()
  return (
    <ChatScreen
      bot={bot}
      locale={locale}
      operators={operators}
      conversationManager={manager}
      button={{ g }}
      {...chatScreenProps}
      online={online}
    />
  )
}

export default App
